import { useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const STATUS_PRESENTATION = {
  green: { bg: "#EBFBEE", border: "#EBFBEE", text: "#2F9E44", label: "Green", stars: 5 },
  yellow: { bg: "#FFF3BF", border: "#FFF3BF", text: "#9A6700", label: "Yellow", stars: 4 },
  orange: { bg: "#FFE8CC", border: "#FFE8CC", text: "#D9480F", label: "Orange", stars: 3 },
  red: { bg: "#FFE3E3", border: "#FFE3E3", text: "#C92A2A", label: "Red", stars: 2 },
  black: { bg: "#343A40", border: "#343A40", text: "#FFFFFF", label: "Black", stars: 1 },
};

const DEFAULT_WEIGHTS = {
  longitudinal_crack: 2,
  transverse_crack: 2,
  alligator_crack: 3,
  pothole: 3,
  other: 0,
};

const formatClassName = (value) => value
  .split("_")
  .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
  .join(" ");

const formatMetric = (value) => {
  if (value == null || Number.isNaN(Number(value))) return "Not available";
  return Number(value).toFixed(2).replace(/\.00$/, "");
};

export default function SurfaceRatingBadge({
  status,
  weightedScore,
  weightedPointsPerKm,
  details,
}) {
  const tooltipId = useId();
  const badgeRef = useRef(null);
  const tooltipRef = useRef(null);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState(null);
  const presentation = STATUS_PRESENTATION[String(status || "").toLowerCase()];

  useLayoutEffect(() => {
    if (!isTooltipOpen) return undefined;

    const updateTooltipPosition = () => {
      if (!badgeRef.current || !tooltipRef.current) return;

      const viewportPadding = 8;
      const gap = 8;
      const badgeRect = badgeRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const left = Math.min(
        Math.max(badgeRect.left, viewportPadding),
        Math.max(viewportPadding, window.innerWidth - tooltipRect.width - viewportPadding),
      );
      const spaceBelow = window.innerHeight - badgeRect.bottom - gap - viewportPadding;
      const top = spaceBelow >= tooltipRect.height
        ? badgeRect.bottom + gap
        : Math.max(viewportPadding, badgeRect.top - tooltipRect.height - gap);

      setTooltipPosition({ left, top });
    };

    updateTooltipPosition();
    window.addEventListener("resize", updateTooltipPosition);
    window.addEventListener("scroll", updateTooltipPosition, true);

    return () => {
      window.removeEventListener("resize", updateTooltipPosition);
      window.removeEventListener("scroll", updateTooltipPosition, true);
    };
  }, [isTooltipOpen]);

  if (!presentation) return <span className="text-[14px] leading-[155%] text-[#828282]">–</span>;

  const segmentLength = details?.segment_length_m ?? 100;
  const thresholds = details?.segment_score_thresholds ?? [2, 4, 7];
  const weights = details?.weights ?? DEFAULT_WEIGHTS;
  const hasCurrentCalculation = Boolean(details?.method_version);
  const weightsText = Object.entries(weights)
    .map(([name, value]) => `${formatClassName(name)} ${value}`)
    .join(" · ");

  const tooltip = (
    <span
      ref={tooltipRef}
      id={tooltipId}
      role="tooltip"
      className={`pointer-events-none fixed z-[2000] w-[330px] max-w-[calc(100vw-16px)] rounded-[8px] border border-[#D8DCE3] bg-white p-[12px] text-left font-['Open_Sans'] text-[12px] font-normal leading-[150%] text-[#343A40] shadow-lg ${tooltipPosition ? "visible opacity-100" : "invisible opacity-0"}`}
      style={tooltipPosition ?? { left: 0, top: 0 }}
    >
      <span className="block font-semibold text-[#242424]">
        Camber Road Surface Condition Rating
      </span>
      <span className="mt-[4px] block text-[#6B7280]">
        Preliminary pavement-condition indicator. It is not an iRAP rating.
      </span>
      {!hasCurrentCalculation && (
        <span className="mt-[6px] block rounded-[4px] bg-[#FFF4E6] px-[8px] py-[6px] text-[#A04B00]">
          Legacy result: reprocess this video to calculate the current segment-based metrics.
        </span>
      )}
      <span className="mt-[8px] block">
        Weighted score: <strong>{formatMetric(weightedScore)}</strong>
        {" · "}Points/km: <strong>{formatMetric(weightedPointsPerKm)}</strong>
      </span>
      <span className="mt-[6px] block">
        Detections are grouped into {segmentLength} m segments. Route stars are
        the distance-weighted mean of segment stars, rounded down.
      </span>
      <span className="mt-[6px] block">
        Weights: {weightsText}.
      </span>
      <span className="mt-[6px] block text-[#6B7280]">
        Segment bands: 0 = 5★; ≤{thresholds[0]} = 4★; ≤{thresholds[1]} = 3★;
        ≤{thresholds[2]} = 2★; above {thresholds[2]} = 1★. The calibration is
        preliminary and will be validated against manually assessed routes.
      </span>
    </span>
  );

  return (
    <>
      <span
        ref={badgeRef}
        tabIndex={0}
        aria-describedby={tooltipId}
        onMouseEnter={() => setIsTooltipOpen(true)}
        onMouseLeave={() => setIsTooltipOpen(false)}
        onFocus={() => setIsTooltipOpen(true)}
        onBlur={() => setIsTooltipOpen(false)}
        className="inline-flex h-[23px] cursor-help items-center justify-center whitespace-nowrap rounded-[182px] border px-[8px] text-[12px] leading-[155%] outline-none focus:ring-2 focus:ring-[#8B7CF6] focus:ring-offset-1"
        style={{
          background: presentation.bg,
          borderColor: presentation.border,
          color: presentation.text,
        }}
      >
        {presentation.label} · {presentation.stars}★
      </span>
      {isTooltipOpen && createPortal(tooltip, document.body)}
    </>
  );
}
